import React, { useMemo } from 'react';
import { View, Text, Platform } from 'react-native';
import Svg, { Path, Circle, Text as SvgText, TSpan, Rect } from 'react-native-svg';
import { ChartConfig } from '../types';

interface PieChartData {
  name: string;
  value: number;
  color: string;
  legendFontColor: string;
  legendFontSize: number;
}

interface PieChartProps {
  data: PieChartData[];
  width: number;
  height: number;
  chartConfig: ChartConfig;
  accessor: string;
  backgroundColor?: string;
  paddingLeft?: string;
  center?: [number, number];
  absolute?: boolean;
  hasLegend?: boolean;
  legendPosition?: 'top' | 'bottom';
  showLabels?: boolean;
  showValues?: boolean;
  showPercentage?: boolean;
  labelColor?: string;
  labelsOutside?: boolean;
  onDataPointClick?: (data: any, index: number) => void;
  isZeroValues?: boolean;
  setChartValue?: (value: string | number) => void;
}

const PieChart: React.FC<PieChartProps> = ({
  data,
  width,
  height,
  chartConfig,
  accessor,
  center = [0, 0],
  hasLegend = true,
  legendPosition = 'top',
  showLabels = true,
  showValues = false,
  showPercentage = true,
  labelColor = '#000000',
  labelsOutside = false,
  onDataPointClick,
  isZeroValues = false,
  setChartValue
}) => {
  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);
  const [tooltipPosition, setTooltipPosition] = React.useState<{ x: number; y: number } | null>(null);
  const showToolTip = true; // Always show tooltip on hover
  const hideTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const hasValidData = data && Array.isArray(data) && data.length > 0;

  const chartData = data;

  const itemsPerRow = Math.floor(width / 80);
  const rows = hasLegend ? Math.ceil(chartData.length / Math.max(itemsPerRow, 1)) : 0;
  const legendHeight = hasLegend ? Math.min(rows * 20 + 12, 60) : 0;
  const chartAreaHeight = height - legendHeight;
  const maxRadius = Math.min(width, chartAreaHeight) / 2.5;
  const radius = maxRadius;

  const centerX = width / 2;
  const centerY = chartAreaHeight / 2;

  const total = chartData.reduce((sum, item) => sum + item.value, 0) || 1;
  let currentAngle = -Math.PI / 2; // Start from top

  const createArcPath = (
    startAngle: number,
    endAngle: number,
    innerRadius = 0
  ) => {
    const x1 = centerX + Math.cos(startAngle) * radius;
    const y1 = centerY + Math.sin(startAngle) * radius;
    const x2 = centerX + Math.cos(endAngle) * radius;
    const y2 = centerY + Math.sin(endAngle) * radius;

    const largeArcFlag = Math.abs(endAngle - startAngle) <= Math.PI ? '0' : '1';
    const sweepFlag = endAngle < startAngle ? '0' : '1';

    return [
      'M',
      centerX,
      centerY,
      'L',
      x1,
      y1,
      'A',
      radius,
      radius,
      0,
      largeArcFlag,
      sweepFlag,
      x2,
      y2,
      'Z',
    ].join(' ');
  };

  const renderLegend = useMemo(() => {
    if (!hasLegend) return null;

    return (
      <View style={{
        paddingHorizontal: 12,
        paddingVertical: 6,
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'center',
      }}>
        {chartData.map((item, index) => (
          <View
            key={index}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              marginRight: 6,
              marginBottom: 6,
            }}
          >
            <View
              style={{
                width: 9,
                height: 9,
                backgroundColor: item.color,
                marginRight: 4,
                borderRadius: 1.5,
              }}
            />
            <Text
              style={{
                color: item.legendFontColor,
                fontSize: 12,
              }}
            >
              {item.name}
            </Text>
          </View>
        ))}
      </View>
    );
  }, [chartData, hasLegend]);

  const sumValue = React.useMemo(() => {
    return data.reduce((sum, item) => sum + item.value, 0) || 0;
  }, [data]);

   const getMouseEvents = (index: number, midAngle?: number) => {
    if (Platform.OS !== 'web') return {};

    return {
      onMouseEnter: () => {
        if (showToolTip) {
          // Clear any pending hide timeout
          if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
          }

          setHoveredIndex(index);
          if (midAngle !== undefined) {
            const tooltipRadius = radius * 1.3;
            const tooltipX = centerX + Math.cos(midAngle) * tooltipRadius;
            const tooltipY = centerY + Math.sin(midAngle) * tooltipRadius;
            setTooltipPosition({ x: tooltipX, y: tooltipY });
          } else {
            setTooltipPosition({ x: centerX, y: centerY - radius - 20 });
          }
        }
      },
      onMouseLeave: () => {
        if (showToolTip) {
          // Delay hiding to allow smooth transition between slices
          hideTimeoutRef.current = setTimeout(() => {
            setHoveredIndex(null);
            setTooltipPosition(null);
          }, 50);
        }
      }
    };
  };

  React.useEffect(() => {
    if(setChartValue) {
      setChartValue(sumValue);
    }
  }, [sumValue]);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  if (!hasValidData) {
    return (
      <View style={{ width, height, backgroundColor: chartConfig.backgroundColor }}>
      </View>
    );
  }

  return (
    <View style={{ backgroundColor: chartConfig.backgroundColor }}>
      {legendPosition === 'top' && renderLegend}
      <Svg width={width} height={chartAreaHeight}>
        {chartData.length === 1 ? (
          (() => {
            const item = chartData[0];
            const percentage = '100.0';

            // Build label components
            const hasPercentage = showLabels && showPercentage;
            const hasValue = showLabels && showValues;

            return (
              <React.Fragment key={0}>
                <Circle
                  cx={centerX}
                  cy={centerY}
                  r={radius}
                  fill={item.color}
                  onPress={() => onDataPointClick?.(item, 0)}
                  onPressIn={() => {
                    if (showToolTip) {
                      if (hideTimeoutRef.current) {
                        clearTimeout(hideTimeoutRef.current);
                        hideTimeoutRef.current = null;
                      }
                      setHoveredIndex(0);
                      setTooltipPosition({ x: centerX, y: centerY - radius - 20 });
                    }
                  }}
                  onPressOut={() => {
                    if (showToolTip) {
                      // Delay hiding to allow smooth transition
                      hideTimeoutRef.current = setTimeout(() => {
                        setHoveredIndex(null);
                        setTooltipPosition(null);
                      }, 50);
                    }
                  }}
                  {...getMouseEvents(0)}
                />
                {showLabels && (hasPercentage || hasValue || item.name) && (
                  <SvgText
                    x={centerX}
                    y={centerY}
                    fill={labelColor}
                    fontSize="12"
                    fontWeight="bold"
                    textAnchor="middle"
                    alignmentBaseline="middle"
                  >
                    {hasPercentage && hasValue ? (
                      <>
                        <TSpan x={centerX} dy="0">{percentage}%</TSpan>
                        <TSpan x={centerX} dy="14">{item.value}</TSpan>
                      </>
                    ) : hasPercentage ? (
                      `${percentage}%`
                    ) : hasValue ? (
                      `${item.value}`
                    ) : (
                      item.name
                    )}
                  </SvgText>
                )}
              </React.Fragment>
            );
          })()
        ) : (
          chartData.map((item, index) => {
            const angle = isZeroValues
              ? (2 * Math.PI) / chartData.length
              : (item.value / total) * 2 * Math.PI;
            const endAngle = currentAngle + angle;
            const path = createArcPath(currentAngle, endAngle);
            const midAngle = currentAngle + angle / 2;
            const labelRadius = labelsOutside ? radius * 1.15 : radius * 0.7;
            const labelX = centerX + Math.cos(midAngle) * labelRadius;
            const labelY = centerY + Math.sin(midAngle) * labelRadius;
            const percentage = isZeroValues
              ? (100 / chartData.length).toFixed(1)
              : ((item.value / total) * 100).toFixed(1);
            const hasPercentage = showLabels && showPercentage;
            const hasValue = showLabels && showValues;

            // Create a larger hit area path for better tooltip detection
            const hitAreaRadius = radius * 1.1;
            const hitAreaPath = createArcPath(currentAngle, endAngle).replace(
              new RegExp(radius.toString(), 'g'),
              hitAreaRadius.toString()
            );

            const result = (
              <React.Fragment key={index}>
                {/* Visible slice */}
                <Path
                  d={path}
                  fill={item.color}
                />
                {/* Invisible larger hit area for better detection */}
                <Path
                  d={hitAreaPath}
                  fill="transparent"
                  stroke="transparent"
                  strokeWidth={20}
                  onPress={() => onDataPointClick?.(item, index)}
                  onPressIn={() => {
                    if (showToolTip) {
                      // Clear any pending hide timeout
                      if (hideTimeoutRef.current) {
                        clearTimeout(hideTimeoutRef.current);
                        hideTimeoutRef.current = null;
                      }
                      setHoveredIndex(index);
                      // Position tooltip outside the slice
                      const tooltipRadius = radius * 1.3;
                      const tooltipX = centerX + Math.cos(midAngle) * tooltipRadius;
                      const tooltipY = centerY + Math.sin(midAngle) * tooltipRadius;
                      setTooltipPosition({ x: tooltipX, y: tooltipY });
                    }
                  }}
                  onPressOut={() => {
                    if (showToolTip) {
                      // Delay hiding to allow smooth transition
                      hideTimeoutRef.current = setTimeout(() => {
                        setHoveredIndex(null);
                        setTooltipPosition(null);
                      }, 50);
                    }
                  }}
                  {...getMouseEvents(index, midAngle)}
                />
                {showLabels && (hasPercentage || hasValue || item.name) && (
                  <SvgText
                    x={labelX}
                    y={labelY}
                    fill={labelColor}
                    fontSize="12"
                    fontWeight="bold"
                    textAnchor="middle"
                    alignmentBaseline="middle"
                  >
                    {hasPercentage && hasValue ? (
                      <>
                        <TSpan x={labelX} dy="0">{percentage}%</TSpan>
                        <TSpan x={labelX} dy="14">{item.value}</TSpan>
                      </>
                    ) : hasPercentage ? (
                      `${percentage}%`
                    ) : hasValue ? (
                      `${item.value}`
                    ) : (
                      item.name
                    )}
                  </SvgText>
                )}
              </React.Fragment>
            );

            currentAngle = endAngle;
            return result;
          })
        )}
        {/* Tooltip */}
        {showToolTip && hoveredIndex !== null && tooltipPosition && chartData[hoveredIndex] && (
          (() => {
            const labelText = chartData[hoveredIndex]?.name || '';
            const fontSize = 10;
            const lineHeight = 14;
            const paddingX = 10;
            const paddingY = 8;
            const hasCJK = /[\u3000-\u303f\u3040-\u309f\u30a0-\u30ff\uff00-\uff9f\u4e00-\u9faf\u3400-\u4dbf]/.test(labelText);
            const charWidth = hasCJK ? fontSize * 1.0 : fontSize * 0.65;

            const margin = 8;
            const maxTooltipWidth = Math.min(width - (margin * 2), 240);

            // Calculate available width for text (reserve space for padding)
            const availableWidth = maxTooltipWidth - (paddingX * 2);
            const maxCharsPerLine = Math.floor(availableWidth / charWidth) - 2;
            const lines: string[] = [];

            if (hasCJK) {
              let currentLine = '';
              const breakableChars = /[・、。，．]/; // Japanese/Chinese punctuation that can be followed by a break

              for (let i = 0; i < labelText.length; i++) {
                const char = labelText[i];
                const testLine = currentLine + char;

                // Check if adding this character would exceed the line length
                if (testLine.length >= maxCharsPerLine) {
                  if (currentLine.length > 0) {
                    lines.push(currentLine);
                    currentLine = char;
                  } else {
                    // Single character is too long, force it anyway
                    lines.push(char);
                    currentLine = '';
                  }
                } else {
                  currentLine = testLine;
                }
              }
              if (currentLine) lines.push(currentLine);
            } else {
              // For non-CJK text, break by words (spaces)
              const words = labelText.split(' ');
              let currentLine = '';

              words.forEach(word => {
                const testLine = currentLine ? `${currentLine} ${word}` : word;
                if (testLine.length > maxCharsPerLine && currentLine) {
                  lines.push(currentLine);
                  currentLine = word;
                } else {
                  currentLine = testLine;
                }
              });
              if (currentLine) lines.push(currentLine);
            }

            // Limit to maximum 3 lines to prevent tooltip from being too tall
            const maxLines = 3;
            const limitedLines = lines.slice(0, maxLines);

            // Truncate last line with ellipsis if there are more lines
            if (lines.length > maxLines) {
              const lastLine = limitedLines[limitedLines.length - 1];
              limitedLines[limitedLines.length - 1] = lastLine.substring(0, Math.max(lastLine.length - 3, 1)) + '...';
            }

            // Truncate each line that exceeds the width
            const truncatedLines = limitedLines.map((line, index) => {
              if (line.length > maxCharsPerLine) {
                return line.substring(0, Math.max(maxCharsPerLine - 3, 1)) + '...';
              }
              return line;
            });

            // Calculate tooltip dimensions - make box hug the text with proper padding
            const maxLineLength = Math.max(...truncatedLines.map(line => line.length), 1);
            const contentWidth = maxLineLength * charWidth;
            const desiredTooltipWidth = contentWidth + (paddingX * 2) + 10;
            const tooltipWidth = Math.min(desiredTooltipWidth, maxTooltipWidth);
            const tooltipHeight = (truncatedLines.length * lineHeight) + (paddingY * 2) + 2;

            const isAtMaxWidth = tooltipWidth >= maxTooltipWidth - 10; // Within 10px of max
            const hasMultipleLines = truncatedLines.length > 1;
            const shouldCenterText = !isAtMaxWidth && !hasMultipleLines;

            // Calculate tooltip position to keep it within bounds
            let tooltipX = tooltipPosition.x - tooltipWidth / 2;
            let tooltipY = tooltipPosition.y - tooltipHeight / 2;

            // Keep tooltip within SVG bounds
            if (tooltipX < margin) tooltipX = margin;
            if (tooltipX + tooltipWidth > width - margin) tooltipX = width - tooltipWidth - margin;
            if (tooltipY < margin) tooltipY = margin;
            if (tooltipY + tooltipHeight > chartAreaHeight - margin) tooltipY = chartAreaHeight - tooltipHeight - margin;

            // Calculate text position based on alignment with proper padding
            const textX = shouldCenterText ? tooltipX + tooltipWidth / 2 : tooltipX + paddingX;
            const textAnchor = shouldCenterText ? 'middle' : 'start';
            const textY = tooltipY + paddingY;

            return (
              <>
                <Rect
                  x={tooltipX}
                  y={tooltipY}
                  width={tooltipWidth}
                  height={tooltipHeight}
                  fill="rgba(0, 0, 0, 0.85)"
                  rx={4}
                  ry={4}
                />
                {truncatedLines.map((line, i) => (
                  <SvgText
                    key={i}
                    x={textX}
                    y={textY + (i * lineHeight) + fontSize}
                    fill="#ffffff"
                    fontSize={fontSize}
                    fontWeight="500"
                    textAnchor={textAnchor}
                  >
                    {line}
                  </SvgText>
                ))}
              </>
            );
          })()
        )}
      </Svg>
      {legendPosition === 'bottom' && renderLegend}
    </View>
  );
};

export default PieChart;
